import React, { useEffect, useMemo, useState } from 'react';

function Field({ label, value }) {
  const v = String(value ?? '').trim();
  if (!v) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, padding: '3px 0' }}>
      <div style={{ fontWeight: 600, color: '#333' }}>{label}</div>
      <div style={{ color: '#222', wordBreak: 'break-word' }}>{v}</div>
    </div>
  );
}

function buildEmergencyLabel(row, idx) {
  const name =
    row[`Emergency ${idx} Name`] ??
    row[`Emergency ${idx} name`] ??
    row[`Emergency ${idx} Contact Name`] ??
    row[`Emergency ${idx}`] ??
    '';
  const phone =
    row[`Emergency ${idx} Phone`] ??
    row[`Emergency ${idx} phone`] ??
    row[`Emergency ${idx} Contact Phone`] ??
    '';

  const n = String(name || '').trim();
  const p = String(phone || '').trim();
  if (!n && !p) return '';
  if (n && p) return `${n} (${p})`;
  return n || p;
}

function splitNameBestEffort(row) {
  const fullName = String(row?.Name || '').trim();

  let first = String(row?.camperFirst || '').trim();
  let last = String(row?.camperLast || '').trim();

  if ((!first || !last) && fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (!first && parts.length) first = parts[0];
    if (!last && parts.length > 1) last = parts.slice(1).join(' ');
  }

  return { first: first || '—', last: last || '—' };
}

export default function NurseIntake({ token, prefillQuery }) {
  const [rows, setRows] = useState([]);
  const [openKey, setOpenKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [showReviewed, setShowReviewed] = useState(false);

  const fetchIntake = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`/.netlify/functions/admin-get-nurse-registration?ts=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setError(json?.error || `Failed to load: ${res.status}`);
        setRows([]);
        return;
      }

      const nextRows = Array.isArray(json.rows) ? json.rows : [];
      setRows(nextRows);

      if (openKey && !nextRows.some((r) => String(r.registrationKey) === String(openKey))) {
        setOpenKey('');
      }
    } catch (e) {
      setError('Network error while loading intake');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchIntake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const q = String(prefillQuery || '').trim();
    if (!q) return;
    setQuery(q);
  }, [prefillQuery]);

  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();

    return (rows || [])
      .filter((r) => {
        if (!showReviewed && r.reviewed) return false;
        return true;
      })
      .filter((r) => {
        if (!q) return true;

        const hay = [
          r.Name,
          r.camperFirst,
          r.camperLast,
          r.DOB,
          r.Email,
          r['Phone Number'],
          r.Allergies || r.Alllergies,
          r.Medications
        ]
          .map((x) => String(x || '').toLowerCase())
          .join(' | ');

        return hay.includes(q);
      });
  }, [rows, query, showReviewed]);

  const setReviewed = async (registrationKey, nextReviewed) => {
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-set-nurse-reviewed', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ registrationKey, reviewed: nextReviewed })
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setError(json?.error || `Failed to update: ${res.status}`);
        return;
      }

      setRows((prev) =>
        prev.map((r) =>
          String(r.registrationKey) === String(registrationKey)
            ? { ...r, reviewed: nextReviewed, reviewedAt: new Date().toISOString() }
            : r
        )
      );
    } catch (e) {
      setError('Network error while updating reviewed state');
    }
  };

  const toggleOpen = (registrationKey) => {
    const k = String(registrationKey || '');
    setOpenKey((prev) => (String(prev) === k ? '' : k));
  };

  // tighter cell styles
  const th = (w) => ({
    width: w,
    maxWidth: w,
    textAlign: 'left',
    padding: '6px 8px',
    borderBottom: '1px solid #eee',
    fontSize: 11,
    color: '#666',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  });

  const td = (w, extra = {}) => ({
    width: w,
    maxWidth: w,
    padding: '6px 8px',
    borderBottom: '1px solid #f1f1f1',
    fontSize: 12,
    overflow: 'hidden',
    ...extra
  });

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
      <div className="card" style={{ maxWidth: '100%' }}>
        {/* Header row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          <div style={{ minWidth: 240 }}>
            <h2 style={{ margin: 0 }}>From registrations</h2>
            <div style={{ marginTop: 4, color: '#555', fontSize: 12 }}>
              Campers who reported allergies and/or medications.
            </div>
          </div>

          <button className="btn" onClick={fetchIntake} disabled={loading} style={{ height: 34 }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Controls row (force wrap inside card) */}
        <div
          style={{
            marginTop: 10,
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 10,
            alignItems: 'center'
          }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, DOB, allergies, meds…"
            style={{
              width: '100%',
              minWidth: 0,
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid #ddd',
              boxSizing: 'border-box'
            }}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, userSelect: 'none', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showReviewed} onChange={(e) => setShowReviewed(e.target.checked)} />
            Show reviewed
          </label>
        </div>

        {error ? <div style={{ marginTop: 10, color: '#b00020' }}>{error}</div> : null}

        <div style={{ marginTop: 10 }}>
          {filtered.length === 0 ? (
            <div style={{ color: '#666', padding: '10px 0', fontSize: 12 }}>No matching campers.</div>
          ) : (
            <div className="table-wrap" style={{ width: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={th(62)}>Rev</th>
                    <th style={th(95)}>First</th>
                    <th style={th(105)}>Last</th>
                    <th style={th(86)}>DOB</th>
                    <th style={th(200)}>Allergies</th>
                    <th style={th(200)}>Meds</th>
                    <th style={th(64)} />
                  </tr>
                </thead>

                <tbody>
                  {filtered.slice(0, 200).map((r, i) => {
                    const registrationKey = String(r.registrationKey || '');
                    const { first, last } = splitNameBestEffort(r);

                    const dob = String(r.DOB || r.dob || '').trim() || '—';
                    const allergies = String(r.Allergies || r.Alllergies || '').trim() || '—';
                    const meds = String(r.Medications || '').trim() || '—';

                    const rowId = registrationKey || `${first}-${last}-${dob}-${i}`;
                    const isOpen = registrationKey && String(openKey) === registrationKey;

                    const emergency1 = buildEmergencyLabel(r, 1);
                    const emergency2 = buildEmergencyLabel(r, 2);

                    return (
                      <React.Fragment key={rowId}>
                        <tr>
                          <td style={td(62)}>
                            <input
                              type="checkbox"
                              checked={!!r.reviewed}
                              onChange={(e) => setReviewed(registrationKey, e.target.checked)}
                              aria-label={`Mark ${r.Name || 'camper'} reviewed`}
                            />
                          </td>

                          <td style={td(95, { fontWeight: 700, whiteSpace: 'nowrap', textOverflow: 'ellipsis' })}>
                            {first}
                          </td>

                          <td style={td(105, { fontWeight: 700, whiteSpace: 'nowrap', textOverflow: 'ellipsis' })}>
                            {last}
                          </td>

                          <td style={td(86, { whiteSpace: 'nowrap' })}>{dob}</td>

                          <td style={td(200, { whiteSpace: 'pre-wrap', wordBreak: 'break-word' })}>{allergies}</td>

                          <td style={td(200, { whiteSpace: 'pre-wrap', wordBreak: 'break-word' })}>{meds}</td>

                          <td style={td(64)}>
                            <button className="btn" type="button" onClick={() => toggleOpen(registrationKey)} style={{ padding: '6px 10px' }}>
                              {isOpen ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>

                        {isOpen ? (
                          <tr>
                            <td colSpan={7} style={{ padding: '10px 10px', borderBottom: '1px solid #f1f1f1', background: '#fafafa' }}>
                              <div style={{ display: 'grid', gap: 2, fontSize: 12 }}>
                                <Field label="Name (raw)" value={r.Name} />
                                <Field label="DOB" value={r.DOB} />
                                <Field label="Email" value={r.Email} />
                                <Field label="Phone" value={r['Phone Number']} />
                                <Field label="Allergies (raw)" value={r.Alllergies} />
                                <Field label="Allergies" value={r.Allergies} />
                                <Field label="Medications" value={r.Medications} />
                                <Field label="Emergency 1" value={emergency1 || r['Emergency 1']} />
                                <Field label="Emergency 2" value={emergency2 || r['Emergency 2']} />
                                <Field label="Reviewed" value={r.reviewed ? 'Yes' : 'No'} />
                                <Field label="Reviewed At" value={r.reviewedAt} />
                                <Field label="Reviewed By" value={r.reviewedBy} />
                                <Field label="Notes" value={r.reviewNotes} />
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {filtered.length > 200 ? (
                <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                  Showing first 200 results.
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div style={{ marginTop: 10, color: '#666', fontSize: 12 }}>© 2026 Camp Registration</div>
      </div>
    </div>
  );
}